import { useCallback, useState } from 'react';

const STORAGE_KEY = 'eis.shell.nav-collapsed';

const read = (): boolean => {
  try {
    return globalThis.localStorage?.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

export interface NavCollapsed {
  readonly collapsed: boolean;
  readonly toggle: () => void;
}

/**
 * Whether the drawer is a rail or a full menu — remembered across reloads.
 *
 * It has to persist, and per browser rather than per session: an operator who collapsed the nav
 * to fit a wide policy grid on a laptop screen did not ask to do it again after every deploy or
 * every F5. It is a device preference, not account state, so `localStorage` is the right store —
 * the same choice `ViewLevelProvider` makes, and for the same reason.
 *
 * Storage can throw (Safari private mode, a locked-down profile). Losing the preference is a
 * shrug; a shell that fails to render is not, so every access is guarded and falls back to
 * expanded — the state in which nothing is hidden from the user.
 */
export function useNavCollapsed(): NavCollapsed {
  const [collapsed, setCollapsed] = useState<boolean>(read);

  const toggle = useCallback(() => {
    setCollapsed((current) => {
      const next = !current;
      try {
        globalThis.localStorage?.setItem(STORAGE_KEY, String(next));
      } catch {
        /* storage unavailable — the in-memory state is still correct for this session */
      }
      return next;
    });
  }, []);

  return { collapsed, toggle };
}
